/**
 * Gauge: a list of dials, each a Number's value plus a scale and zones.
 *
 * The value half (signal or calculation) is all the server sees. Label, unit,
 * decimals, the scale and the zones are drawing settings; they travel in the
 * link (`gd`) so a saved chart, a tile and a shared link draw the same dials.
 * Plan of record: docs/plans/gauge.md.
 */
import type { GaugeDialReq, NumberAgg, PlotSpecGaugeDial } from '../../api/types.ts';
import { NUMBER_AGGS, rewriteNumberSignal } from './number-config.ts';

export const GAUGE_MAX_DIALS = 12;

export type GaugeZoneColor = 'yellow' | 'red';

/** A band on the scale. An empty end runs to that end of the scale. */
export interface GaugeZone {
  color: GaugeZoneColor;
  from: number | null;
  to: number | null;
}

export interface GaugeDialState {
  mode: 'signal' | 'formula';
  column: string;
  agg: NumberAgg;
  expression: string;
  label: string;
  unit: string;
  decimals: number;
  /** Empty: taken from the data (0, or below the lowest value, to a round number above the highest). */
  min: number | null;
  max: number | null;
  /** Numbered divisions; null is Auto. */
  steps: number | null;
  zones: GaugeZone[];
}

export type GaugeLayout = 'ship' | 'sensor' | 'matrix';

export const GAUGE_LAYOUTS: Array<{ value: GaugeLayout; label: string; title: string }> = [
  { value: 'ship', label: 'By ship', title: 'One panel per ship, its dials inside' },
  { value: 'sensor', label: 'By sensor', title: 'One panel per dial, the ships inside' },
  { value: 'matrix', label: 'Matrix', title: 'Ships down, dials across' },
];

export const GAUGE_STEP_CHOICES = Array.from({ length: 19 }, (_, i) => i + 2);

export function newGaugeDial(column = ''): GaugeDialState {
  return {
    mode: 'signal', column, agg: 'latest', expression: '', label: '', unit: '', decimals: 2,
    min: null, max: null, steps: null, zones: [],
  };
}

/** Whether a dial has what the server needs. */
export function dialReady(d: GaugeDialState): boolean {
  return d.mode === 'formula' ? !!d.expression.trim() : !!d.column;
}

export function dialsForRequest(dials: GaugeDialState[]): GaugeDialReq[] {
  return dials.map((d) =>
    d.mode === 'formula'
      ? { mode: 'formula', expression: d.expression }
      : { mode: 'signal', column: d.column, agg: d.agg },
  );
}

/** What a dial reads, as the server echoes it back (`_gauge_key`). */
export function dialKey(d: GaugeDialState): string {
  return d.mode === 'formula' ? `f:${d.expression}` : `${d.agg}:${d.column}`;
}

/** Every signal the dials read, formulas included, in order. */
export function dialSignals(dials: GaugeDialState[]): string[] {
  const out: string[] = [];
  for (const d of dials) {
    if (d.mode === 'signal') { if (d.column) out.push(d.column); continue; }
    const re = /\b(?:latest|avg|sum|min|max|count)\s*\(\s*(?:"((?:\\.|[^"\\])*)"|'([^']*)'|([A-Za-z_]\w*))\s*\)/gi;
    for (const m of d.expression.matchAll(re)) out.push(m[1] ?? m[2] ?? m[3] ?? '');
  }
  return [...new Set(out.filter(Boolean))];
}

/** What a dial is called on screen: its label, else what it reads. */
export function dialTitle(d: GaugeDialState): string {
  if (d.label.trim()) return d.label.trim();
  if (d.mode === 'formula') return d.expression.trim() || 'Calculation';
  return d.column || 'Dial';
}

/* ── Scale ──────────────────────────────────────────────── */

const NICE = [1, 2, 2.5, 5];
const mantissa = (x: number): number => +(x / 10 ** Math.floor(Math.log10(x))).toFixed(6);

/** Decimal places a step needs to be written exactly (0.25 -> 2). */
export function decimalsOf(x: number): number {
  for (let d = 0; d < 7; d++) if (Math.abs(Math.round(x * 10 ** d) - x * 10 ** d) < 1e-6) return d;
  return 6;
}

/** A round step near `raw`: 1, 2, 2.5 or 5 times a power of ten. */
function niceStep(raw: number): number {
  const e = 10 ** Math.floor(Math.log10(raw));
  const m = raw / e;
  return (m < 1.5 ? 1 : m < 2.25 ? 2 : m < 3.5 ? 2.5 : m < 7.5 ? 5 : 10) * e;
}

/**
 * Auto steps: 4 to 12 even divisions on a 1, 2, 2.5 or 5 step, the count
 * nearest 8 (more on a tie) - 0-100 in tens, 0-14 in twos, 160-240 in tens.
 * Null when no such division exists (0-16,800), and the caller falls back to
 * a round step with the ends labelled.
 */
export function autoDivisions(range: number): number | null {
  let best: number | null = null;
  for (let n = 4; n <= 12; n++) {
    if (!NICE.includes(mantissa(range / n))) continue;
    if (best === null || Math.abs(n - 8) < Math.abs(best - 8) || (Math.abs(n - 8) === Math.abs(best - 8) && n > best)) best = n;
  }
  return best;
}

export interface GaugeScale {
  min: number;
  max: number;
  step: number;
  /** Numbered ticks, ends included. */
  majors: number[];
  minors: number[];
  /** Minor intervals per major one. */
  subs: number;
  /** Thousands: tick numbers written as 2.5k. */
  big: boolean;
}

export function gaugeScale(min: number, max: number, steps: number | null): GaugeScale {
  const range = max - min;
  let step: number;
  let even = true;
  if (steps) step = range / steps;
  else {
    const n = autoDivisions(range);
    if (n) step = range / n;
    else { even = false; step = niceStep(range / 8); }
  }
  const subs = ({ 1: 5, 2: 4, 2.5: 5, 5: 5 } as Record<number, number>)[mantissa(step)] ?? 2;
  const majors: number[] = [];
  for (let v = Math.ceil((min - 1e-9) / step) * step; v <= max + step * 1e-6; v += step) majors.push(+v.toFixed(9));
  if (steps) {
    // Exact divisions from min, not a grid from zero.
    majors.length = 0;
    for (let i = 0; i <= steps; i++) majors.push(+(min + step * i).toFixed(9));
  } else if (!even) {
    if (!majors.length || majors[0]! - min > step * 0.4) majors.unshift(min); else majors[0] = min;
    if (max - majors.at(-1)! > step * 0.4) majors.push(max); else majors[majors.length - 1] = max;
  }
  const minors: number[] = [];
  for (let i = 0; i < majors.length - 1; i++) {
    const a = majors[i]!;
    const s = (majors[i + 1]! - a) / subs;
    for (let j = 1; j < subs; j++) minors.push(a + s * j);
  }
  return { min, max, step, majors, minors, subs, big: Math.max(Math.abs(min), Math.abs(max)) >= 10000 };
}

/** Divisions Auto would choose, for the Steps menu ("Auto (10)"). */
export function autoStepCount(min: number, max: number): number {
  const sc = gaugeScale(min, max, null);
  return Math.round((max - min) / sc.step);
}

/**
 * The dial's scale ends. A set end is used as it is. An empty minimum is 0
 * when every value is at or above it, else a round number below the lowest; an
 * empty maximum is a round number above the highest - with room, so a needle
 * at the top of its day is not pinned to the end stop. Null when the ends are
 * set the wrong way round.
 */
export function dialRange(d: GaugeDialState, values: Array<number | null>): { min: number; max: number } | null {
  const seen = values.filter((v): v is number => v != null && Number.isFinite(v));
  const lo = seen.length ? Math.min(...seen) : 0;
  const hi = seen.length ? Math.max(...seen) : 0;
  let min = d.min;
  let max = d.max;
  if (min == null) {
    if (lo >= 0 && (max == null || max > 0)) min = 0;
    else {
      const span = Math.max(Math.abs(max ?? hi) - lo, Math.abs(lo), 1e-9);
      const st = niceStep(span / 5);
      min = Math.floor(lo / st) * st;
    }
  }
  if (max == null) {
    const top = Math.max(hi, min);
    const span = Math.max((top - min) * 1.15, Math.abs(top) * 1e-3, 1e-9);
    const st = niceStep(span / 5);
    max = Math.ceil((min + span) / st) * st;
    if (max <= min) max = min + st * 5;
  }
  return max > min ? { min, max } : null;
}

/** Which zone a value is in, red first where they overlap. */
export function zoneAt(zones: GaugeZone[], min: number, max: number, v: number | null): GaugeZoneColor | null {
  if (v == null || !Number.isFinite(v)) return null;
  const c = Math.min(max, Math.max(min, v));
  const hit = (z: GaugeZone): boolean => c >= (z.from ?? min) && c <= (z.to ?? max);
  if (zones.some((z) => z.color === 'red' && hit(z))) return 'red';
  if (zones.some((z) => z.color === 'yellow' && hit(z))) return 'yellow';
  return null;
}

/**
 * Decimal places for a dial's tick numbers: as many as the numbers actually
 * written need, all alike - 0, 5, 10 rather than 0.0, 5.0, 10.0 when only
 * every other tick of a 2.5 step is labelled.
 */
export function tickDecimals(values: number[], sc: GaugeScale): number {
  const scaled = sc.big ? values.map((v) => v / 1000) : values;
  return Math.min(3, Math.max(0, ...scaled.map(decimalsOf)));
}

/** A tick number: 2.5k past ten thousand, else with `dp` places (see `tickDecimals`). */
export function tickText(v: number, sc: GaugeScale, dp = tickDecimals(sc.majors, sc)): string {
  const fmt = (x: number, min: number, max: number): string =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: min, maximumFractionDigits: max }).format(Object.is(x, -0) ? 0 : x);
  // Thousands read as a count of k - 14k beside 16.8k, not 14.0k.
  if (sc.big) return v === 0 ? '0' : `${fmt(v / 1000, 0, dp)}k`;
  return fmt(v, dp, dp);
}

/* ── Link ───────────────────────────────────────────────── */

interface WireDial {
  c?: string; a?: string; m?: 'f'; e?: string;
  l?: string; u?: string; dp?: number;
  lo?: number; hi?: number; st?: number;
  z?: Array<[string, number | null, number | null]>;
}

function toWire(d: GaugeDialState): WireDial {
  const w: WireDial = {};
  if (d.mode === 'formula') { w.m = 'f'; w.e = d.expression; } else {
    w.c = d.column;
    if (d.agg !== 'latest') w.a = d.agg;
  }
  if (d.label) w.l = d.label;
  if (d.unit) w.u = d.unit;
  if (d.decimals !== 2) w.dp = d.decimals;
  if (d.min != null) w.lo = d.min;
  if (d.max != null) w.hi = d.max;
  if (d.steps != null) w.st = d.steps;
  if (d.zones.length) w.z = d.zones.map((z) => [z.color === 'red' ? 'r' : 'y', z.from, z.to]);
  return w;
}

const finite = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

function fromWire(o: unknown): GaugeDialState | null {
  if (!o || typeof o !== 'object') return null;
  const w = o as Record<string, unknown>;
  const d = newGaugeDial();
  if (w.m === 'f') {
    if (typeof w.e !== 'string' || !w.e.trim()) return null;
    d.mode = 'formula';
    d.expression = w.e.slice(0, 1000);
  } else {
    if (typeof w.c !== 'string' || !w.c) return null;
    d.column = w.c.slice(0, 250);
    if (NUMBER_AGGS.some((a) => a.value === w.a)) d.agg = w.a as NumberAgg;
  }
  if (typeof w.l === 'string') d.label = w.l.slice(0, 120);
  if (typeof w.u === 'string') d.unit = w.u.slice(0, 40);
  const dp = finite(w.dp);
  if (dp != null) d.decimals = Math.max(0, Math.min(10, Math.round(dp)));
  d.min = finite(w.lo);
  d.max = finite(w.hi);
  const st = finite(w.st);
  if (st != null && GAUGE_STEP_CHOICES.includes(st)) d.steps = st;
  if (Array.isArray(w.z)) {
    for (const z of w.z.slice(0, 12)) {
      if (!Array.isArray(z)) continue;
      d.zones.push({ color: z[0] === 'r' ? 'red' : 'yellow', from: finite(z[1]), to: finite(z[2]) });
    }
  }
  return d;
}

export function serializeGauge(
  s: { gauge_dials: GaugeDialState[]; gauge_layout: GaugeLayout },
  params: URLSearchParams,
): void {
  if (s.gauge_dials.length) params.set('gd', JSON.stringify(s.gauge_dials.map(toWire)));
  if (s.gauge_layout !== 'ship') params.set('glay', s.gauge_layout);
}

/** `gd`: anything malformed is dropped rather than half-applied. */
export function parseGaugeDials(raw: string | null): GaugeDialState[] | null {
  if (!raw) return null;
  let data: unknown;
  try { data = JSON.parse(raw); } catch { return null; }
  if (!Array.isArray(data)) return null;
  return data.slice(0, GAUGE_MAX_DIALS).map(fromWire).filter((d): d is GaugeDialState => !!d);
}

export function parseGaugeLayout(raw: string | null): GaugeLayout | null {
  return raw === 'ship' || raw === 'sensor' || raw === 'matrix' ? raw : null;
}

/** "Gauge · Main engine load, Hotel load" for catalog cards and tile titles. */
export function describeGauge(params: URLSearchParams): string {
  const dials = parseGaugeDials(params.get('gd')) ?? [];
  if (!dials.length) return 'Gauge';
  const names = dials.map(dialTitle);
  return names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3}` : names.join(', ');
}

/**
 * A derived signal was renamed (`replacement`) or removed (null). A removed
 * signal empties its dial rather than deleting it, so the label, scale and
 * zones someone set up survive for the next signal - as Number keeps its
 * label when its signal goes.
 */
export function rewriteGaugeSignal(dials: GaugeDialState[], name: string, replacement: string | null): GaugeDialState[] {
  return dials.map((d) => {
    if (d.mode === 'signal') return d.column === name ? { ...d, column: replacement ?? '' } : d;
    const expression = rewriteNumberSignal(d.expression, name, replacement);
    return expression === d.expression ? d : { ...d, expression };
  });
}

/* ── The Assistant's proposal shape ─────────────────────── */

/** A proposed dial (`GaugeDialSpec`) as editor state. */
export function dialFromSpec(d: PlotSpecGaugeDial): GaugeDialState {
  const dial = newGaugeDial(d.column ?? '');
  if (d.mode === 'formula') { dial.mode = 'formula'; dial.expression = d.expression ?? ''; }
  if (d.agg) dial.agg = d.agg;
  dial.label = d.label ?? '';
  dial.unit = d.unit ?? '';
  if (d.decimals != null) dial.decimals = d.decimals;
  dial.min = d.min ?? null;
  dial.max = d.max ?? null;
  dial.steps = d.steps ?? null;
  dial.zones = (d.zones ?? []).map((z) => ({ color: z.color, from: z.start ?? null, to: z.end ?? null }));
  return dial;
}

/** Editor state back to the proposal shape, for a saved chart the Assistant reads. */
export function dialToSpec(d: GaugeDialState): PlotSpecGaugeDial {
  return {
    mode: d.mode,
    ...(d.mode === 'formula' ? { expression: d.expression } : { column: d.column, agg: d.agg }),
    label: d.label, unit: d.unit, decimals: d.decimals,
    min: d.min, max: d.max, steps: d.steps,
    zones: d.zones.map((z) => ({ color: z.color, start: z.from, end: z.to })),
  };
}
